import React, { useState, useEffect, useRef } from "react";
import { object, array, shape, string, bool } from "prop-types";
import cx from "classnames";
import uniqueid from "lodash.uniqueid";

import debounce from "lodash.debounce";

import SiteLogo from "../SiteLogo/SiteLogo.jsx";
import SearchBox from "../SearchBox/SearchBox.jsx";
import MenuButton from "../MenuButton/MenuButton.jsx";

import useClickAway from "../../common/useClickAway.js";

import GlobalNav from "./GlobalNav/GlobalNav.jsx";

const propTypes = {
  logo: object,
  theme: string,
  sticky: bool,
  primaryNav: array,
  search: object,
  backgroundColor: string,
  skipNavigation: shape({
    text: string,
    href: string,
  }),
  dictionary: shape({
    openMenuText: string,
    closeMenuText: string,
  }),
  showVisibleLabel: bool,
};

const SiteHeaderBase = (props) => {
  const {
    logo,
    sticky = false,
    primaryNav,
    search,
    skipNavigation = {},
    theme,
    dictionary,
    backgroundColor,
    showVisibleLabel = false,
  } = props;

  const breakpoint = 800;
  const controlID = uniqueid("site-header__panel-");

  const [isMobile, setIsMobile] = useState(window.innerWidth <= breakpoint);
  const [isOpen, setIsOpen] = useState(false);
  const [isSticky, setIsSticky] = useState(false);

  const siteHeaderRef = useRef();
  const menuButtonRef = useRef();
  const stickyTriggerRef = useRef();
  const toggleMenuRef = useClickAway({
    isOpen: isOpen,
    setOpen: setIsOpen,
  });

  const openMenu = () => {
    if (!sticky) {
      if (siteHeaderRef.current) {
        siteHeaderRef.current.scrollIntoView();
      } else {
        window.scrollTo(0, 0);
      }
    }
    setIsOpen(true);
  };

  const closeMenu = (moveFocus) => {
    setIsOpen(false);

    if (moveFocus) {
      menuButtonRef.current.focus();
    }
  };

  const handleMenuClick = (e) => {
    isOpen ? closeMenu(false) : openMenu();
  };

  const handleNavKeyDown = (event, index) => {
    if (!isMobile) {
      return;
    }

    const links = document.querySelectorAll(".js-global-nav__focus-ref");
    const lastIndex = links.length - 1;
    const first = index === 0;
    const last = index === lastIndex;

    switch (event.key) {
      case "Up":
      case "ArrowUp":
        event.preventDefault();
        if (first) {
          links[lastIndex].focus();
        } else {
          links[index - 1].focus();
        }
        break;
      case "Down":
      case "ArrowDown":
        event.preventDefault();
        if (last) {
          links[0].focus();
        } else {
          links[index + 1].focus();
        }
        break;
      case "Tab":
        if (event.shiftKey) {
          if (first) {
            event.preventDefault();
            closeMenu(true);
          }
        } else {
          if (last) {
            closeMenu(false);
          }
        }
        break;
      default:
    }
  };

  useEffect(() => {
    const handleResize = debounce(() => {
      // render is only called if this actually changes state.
      setIsMobile(window.innerWidth <= breakpoint);
    }, 10);

    window.addEventListener("resize", handleResize);

    return () => window.removeEventListener("resize", handleResize);
  }, [breakpoint]);

  useEffect(() => {
    const observer = new IntersectionObserver((entries, observer) => {
      entries.forEach((entry) => {
        setIsSticky(!entry.isIntersecting);
      });
    });

    const el = stickyTriggerRef.current;

    if (el) {
      observer.observe(el);
    }

    return () => {
      observer.unobserve(el);
    };
  }, [stickyTriggerRef]);

  useEffect(() => {
    const handleScrollLock = (e) => {
      siteHeaderRef?.current.scrollIntoView();
    };

    const handleKeydown = (e) => {
      if (e.key === "Esc" || e.key === "Escape") {
        closeMenu(isOpen);
      }
    };

    // pause videos from playing behind the screen.
    document.querySelectorAll("video").forEach((video) => {
      if (isOpen) {
        if (!video.paused) {
          video.pause();
        }
      } else {
        if (video.loop && video.autoplay) {
          video.play();
        }
      }
    });

    if (isOpen && !sticky) {
      document.addEventListener("keydown", handleKeydown);
      document.addEventListener("scroll", handleScrollLock);
    } else {
      document.removeEventListener("keydown", handleKeydown);
      document.removeEventListener("scroll", handleScrollLock);
    }
    return () => {
      document.removeEventListener("keydown", handleKeydown);
      document.removeEventListener("scroll", handleScrollLock);
    };
  }, [isOpen, sticky]);

  const renderPrimaryNav = () => (
    <nav
      className={cx("site-header__navigation", {
        "site-header__navigation--mobile": isMobile,
        "site-header__navigation--desktop": !isMobile,
        "site-header__navigation--open": isOpen,
      })}
      aria-label="Primary"
      id={controlID}
    >
      {primaryNav.map((el, i) => (
        <GlobalNav
          key={i}
          {...el}
          theme={theme}
          dictionary={dictionary}
          introBlock={
            el.link?.href && el.title
              ? {
                title: el.title,
                subtitle: el.subtitle,
                link: el.link,
              }
              : null
          }
          menuLabel={el.title || el.link.text}
          isMobile={isMobile}
          topLevelKeyDown={(e) => handleNavKeyDown(e, i)}
        />
      ))}
    </nav>
  );

  return (
    <>
      {sticky && <div ref={stickyTriggerRef}></div>}
      <div
        className={cx("site-header__background", {
          "site-header__background--sticky": isSticky,
          "site-header__background--dark theme-dark": theme === "dark",
          "site-header__background--background": backgroundColor,
        })}
        style={{ "--background-color": backgroundColor }}
        ref={siteHeaderRef}
      >
        <div
          className={cx("site-header__container container", {
            "site-header__container--mobile": isMobile,
            "site-header__container--desktop": !isMobile,
            "site-header__container--sticky": isSticky,
          })}
        >
          <a
            className="site-header__skip-nav"
            href={skipNavigation.href || "#main-content"}
          >
            {skipNavigation.text || "Skip Navigation"}
          </a>
          <div
            className={cx("site-header__item-container", {
              "site-header__item-container--search": search,
            })}
          >
            {isMobile && (
              <div className="site-header__toggle-menu" ref={toggleMenuRef}>
                <MenuButton
                  classNames="site-header__menu-button"
                  isOpen={isOpen}
                  controlID={controlID}
                  onClick={handleMenuClick}
                  reversed={false}
                  buttonRef={menuButtonRef}
                  openText={dictionary.openMenuText}
                  closeText={dictionary.closeMenuText}
                  showVisibleLabel={showVisibleLabel}
                />
                {primaryNav && renderPrimaryNav()}
              </div>
            )}
            <div className="site-header__logo">
              <SiteLogo {...logo} />
            </div>
            {!isMobile && primaryNav && renderPrimaryNav()}
            {search && (
              <div className="site-header__search">
                <SearchBox {...search} textInput={{}} />
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
};
SiteHeaderBase.propTypes = propTypes;
export default SiteHeaderBase;
